import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { CreateServiceAccountData, UpdateServiceAccountData, ServiceAccount } from '@/shared/types/serviceAccountTypes';
import { PaginatedResponse } from '@/shared/types/responseTypes';

// Use Account Service Accounts Query
export const useAccountServiceAccounts = () => {
  return useQuery({
    queryKey: ['service-accounts'],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<ServiceAccount>>(
        `/api/v1/service-accounts`,
      );
      return response.data.results;
    },
  });
};

// Create Account Service Account Mutation
export const useCreateAccountServiceAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceAccountData }: { serviceAccountData: CreateServiceAccountData }) => {
      return await apiClient.post<APIMessageResponse>(
        `/api/v1/service-accounts`,
        {
          body: serviceAccountData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-accounts'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

export const useUpdateAccountServiceAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceAccountId, serviceAccountData }: { serviceAccountId: string; serviceAccountData: UpdateServiceAccountData }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-accounts/${serviceAccountId}`,
        {
          body: serviceAccountData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-accounts'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

// Delete Account Service Account Mutation
export const useRemoveServiceAccountFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceAccountId }: { serviceAccountId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/service-accounts/${serviceAccountId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-accounts'] });
    },
    onError: (error) => {
      return error;
    },
  });
};